import React, { useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Button } from '../../components';

import css from './TwilioVerify.module.css';

// Mirror tph-api's E.164 convention (`/^\+\d{6,15}$/`).
// Exported (with the formatters below) so the signup phone field shares one
// source of truth for the US auto-format behavior.
export const E164 = /^\+\d{6,15}$/;

// US auto-formatting (Story 9.13 design pass): users type digits in any common
// shape ("8168983331", "816-898-3331", "(816) 898-3331"), we display
// "(816) 898-3331" and normalize to "+18168983331" for the API. A leading "+"
// preserves full international E.164 entry unchanged.
export const onlyDigits = value => (value || '').replace(/\D/g, '');

export const usDigitsFrom = raw => {
  let d = onlyDigits(raw);
  // Tolerate a US country-code prefix ("1 816 898 3331").
  if (d.length === 11 && d.startsWith('1')) {
    d = d.slice(1);
  }
  return d.slice(0, 10);
};

export const formatUsDigits = d => {
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
};

export const isInternational = value => (value || '').trim().startsWith('+');

// Only these tph-api error codes carry messages written for end users; anything
// else (e.g. validation_failed's Joi messages echo user input and internal field
// names) must fall back to the local i18n strings.
const DISPLAYABLE_ERROR_CODES = ['locked_out', 'code_expired', 'invalid_code'];

const displayableServerMessage = body =>
  DISPLAYABLE_ERROR_CODES.includes(body?.error?.code) ? body.error.message : null;

// Story 5.51: tph-api refuses a bad number with this code. The copy is local
// (TwilioVerify.numberRejected); the server's message is not customer copy.
const NUMBER_REJECTED_CODE = 'invalid_phone_number';

const SIX_DIGITS = /^\d{6}$/;

// E.164 value sent to the verify API for the current display value.
export const toE164 = display =>
  isInternational(display) ? display.trim() : `+1${onlyDigits(display)}`;

export const isValidPhone = display =>
  isInternational(display) ? E164.test(display.trim()) : onlyDigits(display).length === 10;

// A known number arrives in E.164; re-display US numbers in the national format.
const toDisplay = value => {
  const m = /^\+1(\d{10})$/.exec((value || '').trim());
  return m ? formatUsDigits(m[1]) : value || '';
};

/**
 * Story 9.13 (AC4) — the reusable phone-verification component (UX-DR2's single
 * shared component, one product config). Built here and wired ONLY into the
 * onboarding modal's Screen 2; the other UX-DR2 surfaces will reuse it.
 *
 * Presentational only (no Redux, no direct network): the host passes
 * `onVerifyStart` / `onVerifyCheck` (which return `{ ok, status, body }` from the
 * same-origin verify proxies) and `onVerified` (fired on an approved check).
 * Manages its own local UI state: phone (editable, US auto-formatted, E.164 on
 * the wire, pre-fillable), the code-entry reveal after a successful start (with
 * a resend link), the 429 lockout (A6 verbatim copy from the upstream body) and
 * the post-SMS voice fallback. `onPhaseChange` lets the host swap its title/body
 * when the code phase is shown (Maroon Crown phase-2 copy).
 *
 * @component
 * @param {Object} props
 * @param {string} [props.initialPhone] pre-filled known number (frames "is this still your number?")
 * @param {Function} props.onVerifyStart ({phone, channel}) => Promise<{ok,status,body}>
 * @param {Function} props.onVerifyCheck ({phone, code}) => Promise<{ok,status,body}>
 * @param {Function} props.onVerified () => void, called once a check is approved
 * @param {Function} [props.onPhaseChange] ({codeSent, displayNumber}) => void, fired when the code phase toggles
 * @returns {JSX.Element}
 */
const TwilioVerify = props => {
  const { initialPhone, onVerifyStart, onVerifyCheck, onVerified, onPhaseChange } = props;
  const intl = useIntl();

  const [phone, setPhone] = useState(toDisplay(initialPhone));
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState(null);
  const [lockoutMessage, setLockoutMessage] = useState(null);
  const [smsAttempted, setSmsAttempted] = useState(false);

  const prefilled = !!initialPhone;
  const phoneValid = isValidPhone(phone);

  // Host phase swap — fires on mount too, so a remount always resets the host
  // back to phase-1 copy. The phone input is disabled while codeSent, so the
  // reported displayNumber is the number the code went to.
  useEffect(() => {
    if (onPhaseChange) {
      onPhaseChange({ codeSent, displayNumber: phone });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeSent]);

  const handlePhoneChange = raw => {
    setPhone(isInternational(raw) ? raw : formatUsDigits(usDigitsFrom(raw)));
  };

  const handleStart = async (channel = 'sms') => {
    if (!phoneValid || inProgress) {
      return;
    }
    setInProgress(true);
    setError(null);
    setLockoutMessage(null);
    // Mark the SMS attempt as made so the voice fallback is offered even when the
    // attempt comes back locked-out (AC4: voice fallback after SMS attempts).
    if (channel === 'sms') {
      setSmsAttempted(true);
    }
    try {
      const { ok, status, body } = await onVerifyStart({ phone: toE164(phone), channel });
      if (ok) {
        setCodeSent(true);
      } else if (status === 429) {
        // A6 verbatim lockout copy from tph-api — nested at body.error.message in
        // the §4.2.2 envelope; fall back to a local string.
        setLockoutMessage(
          displayableServerMessage(body) || intl.formatMessage({ id: 'TwilioVerify.lockout' })
        );
      } else if (body?.error?.code === NUMBER_REJECTED_CODE) {
        setError(intl.formatMessage({ id: 'TwilioVerify.numberRejected' }));
      } else {
        setError(
          displayableServerMessage(body) || intl.formatMessage({ id: 'TwilioVerify.startFailed' })
        );
      }
    } catch (e) {
      setError(intl.formatMessage({ id: 'TwilioVerify.startFailed' }));
    } finally {
      setInProgress(false);
    }
  };

  const codeValid = SIX_DIGITS.test(code);

  const handleCheck = async () => {
    if (!codeValid || inProgress) {
      return;
    }
    setInProgress(true);
    setError(null);
    setLockoutMessage(null);
    try {
      const { ok, status, body } = await onVerifyCheck({
        phone: toE164(phone),
        code,
      });
      if (ok) {
        onVerified();
      } else if (status === 429) {
        setLockoutMessage(
          displayableServerMessage(body) || intl.formatMessage({ id: 'TwilioVerify.lockout' })
        );
      } else {
        setError(
          displayableServerMessage(body) || intl.formatMessage({ id: 'TwilioVerify.invalidCode' })
        );
      }
    } catch (e) {
      setError(intl.formatMessage({ id: 'TwilioVerify.invalidCode' }));
    } finally {
      setInProgress(false);
    }
  };

  const editPhone = () => {
    setCodeSent(false);
    setCode('');
    setError(null);
  };

  return (
    <div className={css.root}>
      {!codeSent ? (
        <>
          <label className={css.label} htmlFor="twilioVerifyPhone">
            {prefilled ? (
              <FormattedMessage id="TwilioVerify.confirmPhoneLabel" />
            ) : (
              <FormattedMessage id="TwilioVerify.enterPhoneLabel" />
            )}
          </label>
          <input
            id="twilioVerifyPhone"
            className={css.input}
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={e => handlePhoneChange(e.target.value)}
            placeholder="(816) 555-1234"
            aria-invalid={phone.length > 0 && !phoneValid}
          />
          {phone.length > 0 && !phoneValid ? (
            <p className={css.error} role="alert">
              <FormattedMessage id="TwilioVerify.invalidPhone" />
            </p>
          ) : null}
          <Button
            rootClassName={css.cta}
            type="button"
            inProgress={inProgress}
            disabled={!phoneValid}
            onClick={() => handleStart('sms')}
          >
            <FormattedMessage id="TwilioVerify.sendCode" />
          </Button>
        </>
      ) : (
        <div className={css.codeStep}>
          <label className={css.label} htmlFor="twilioVerifyCode">
            <FormattedMessage id="TwilioVerify.codeLabel" />
          </label>
          <input
            id="twilioVerifyCode"
            className={`${css.input} ${css.codeInput}`}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={e => setCode(onlyDigits(e.target.value).slice(0, 6))}
          />
          <Button
            rootClassName={css.cta}
            type="button"
            inProgress={inProgress}
            disabled={!codeValid}
            onClick={handleCheck}
          >
            <FormattedMessage id="TwilioVerify.verify" />
          </Button>
          <div className={css.links}>
            <Button rootClassName={css.link} type="button" onClick={() => handleStart('sms')}>
              <FormattedMessage id="TwilioVerify.resendCode" />
            </Button>
            <Button rootClassName={css.link} type="button" onClick={editPhone}>
              <FormattedMessage id="TwilioVerify.editPhone" />
            </Button>
          </div>
        </div>
      )}

      {error ? (
        <p className={codeSent ? css.errorBelow : css.error} role="alert">
          {error}
        </p>
      ) : null}

      {lockoutMessage ? (
        <div className={css.lockout} role="alert">
          <p>{lockoutMessage}</p>
          {smsAttempted ? (
            <Button rootClassName={css.link} type="button" onClick={() => handleStart('voice')}>
              <FormattedMessage id="TwilioVerify.voiceFallback" />
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default TwilioVerify;
